// Voice notes from Android (Gary, 15 Oct): Chrome records Opus sound in a WebM file, which
// WhatsApp refuses; WhatsApp takes Opus in an Ogg file. The sound itself is the same, so this
// repackages it without re-encoding: read the Opus packets out of the WebM (Matroska) file and
// write them into Ogg pages (RFC 7845). Pure, no packages; tested in webmToOgg.test.ts.

const ID_SEGMENT = 0x18538067;
const ID_CLUSTER = 0x1f43b675;
const ID_TRACKS = 0x1654ae6b;
const ID_TRACK_ENTRY = 0xae;
const ID_AUDIO = 0xe1;
const ID_BLOCK_GROUP = 0xa0;
const ID_CODEC_ID = 0x86;
const ID_CODEC_PRIVATE = 0x63a2;
const ID_CHANNELS = 0x9f;
const ID_SIMPLE_BLOCK = 0xa3;
const ID_BLOCK = 0xa1;

// Containers we step into rather than over.
const MASTERS = new Set([ID_SEGMENT, ID_CLUSTER, ID_TRACKS, ID_TRACK_ENTRY, ID_AUDIO, ID_BLOCK_GROUP]);

function vintLength(first: number): number {
  for (let n = 1; n <= 8; n += 1) if (first & (0x80 >> (n - 1))) return n;
  return 0;
}

/** Reads an element id (marker bits kept) at `at`. */
function readId(b: Uint8Array, at: number): { id: number; len: number } | null {
  const len = vintLength(b[at] ?? 0);
  if (!len || len > 4 || at + len > b.length) return null;
  let id = 0;
  for (let i = 0; i < len; i += 1) id = id * 256 + (b[at + i] ?? 0);
  return { id, len };
}

/** Reads an element size at `at`; `size` is null for "unknown" (all ones), as live recordings use. */
function readSize(b: Uint8Array, at: number): { size: number | null; len: number } | null {
  const len = vintLength(b[at] ?? 0);
  if (!len || at + len > b.length) return null;
  let v = (b[at] ?? 0) & (0xff >> len);
  let allOnes = v === (0xff >> len);
  for (let i = 1; i < len; i += 1) {
    const x = b[at + i] ?? 0;
    if (x !== 0xff) allOnes = false;
    v = v * 256 + x;
  }
  return { size: allOnes ? null : v, len };
}

/** Where the frame data starts inside a (Simple)Block: after the track number, timecode and flags. */
function blockFrame(data: Uint8Array): Uint8Array | null {
  const track = vintLength(data[0] ?? 0);
  if (!track) return null;
  const flags = data[track + 2] ?? 0;
  // Lacing (several frames in one block) is not used by browser recorders; refuse it rather than guess.
  if (flags & 0x06) return null;
  return data.subarray(track + 3);
}

export interface WebmOpus { opusHead: Uint8Array | null; channels: number; packets: Uint8Array[] }

/** The Opus packets (and the OpusHead, when present) from a WebM recording. Null if it is not WebM Opus. */
export function readWebmOpus(b: Uint8Array): WebmOpus | null {
  if (readId(b, 0)?.id !== 0x1a45dfa3) return null; // not EBML
  let codec = '';
  let opusHead: Uint8Array | null = null;
  let channels = 1;
  const packets: Uint8Array[] = [];
  let at = 0;
  while (at < b.length) {
    const id = readId(b, at);
    if (!id) break;
    const size = readSize(b, at + id.len);
    if (!size) break;
    const start = at + id.len + size.len;
    if (MASTERS.has(id.id)) { at = start; continue; } // step inside
    if (size.size === null) break; // only containers may have an unknown size
    const end = Math.min(start + size.size, b.length);
    const data = b.subarray(start, end);
    if (id.id === ID_CODEC_ID) codec = new TextDecoder().decode(data);
    else if (id.id === ID_CODEC_PRIVATE) opusHead = data.slice();
    else if (id.id === ID_CHANNELS) channels = data.reduce((v, x) => v * 256 + x, 0) || 1;
    else if (id.id === ID_SIMPLE_BLOCK || id.id === ID_BLOCK) {
      const frame = blockFrame(data);
      if (frame && frame.length) packets.push(frame.slice());
    }
    at = end;
  }
  if (codec && codec !== 'A_OPUS') return null;
  if (!packets.length) return null;
  return { opusHead, channels, packets };
}

/** How many 48 kHz samples one Opus packet holds, from its first byte (RFC 6716, 3.1). */
export function opusSamples(packet: Uint8Array): number {
  const toc = packet[0] ?? 0;
  const config = toc >> 3;
  const frameMs = config < 12 ? [10, 20, 40, 60][config & 3]! : config < 16 ? [10, 20][config & 1]! : [2.5, 5, 10, 20][config & 3]!;
  const code = toc & 3;
  const frames = code === 0 ? 1 : code === 3 ? ((packet[1] ?? 0) & 0x3f) : 2;
  return Math.round(frameMs * 48) * frames;
}

// ---------------------------------------------------------------- Ogg

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let r = i << 24;
    for (let k = 0; k < 8; k += 1) r = (r & 0x80000000) ? ((r << 1) ^ 0x04c11db7) : (r << 1);
    t[i] = r >>> 0;
  }
  return t;
})();

export function oggCrc(page: Uint8Array): number {
  let crc = 0;
  for (const byte of page) crc = ((crc << 8) ^ CRC_TABLE[((crc >>> 24) ^ byte) & 0xff]!) >>> 0;
  return crc >>> 0;
}

function oggPage(packet: Uint8Array, granule: number, serial: number, seq: number, type: number): Uint8Array {
  const lacing: number[] = [];
  let left = packet.length;
  while (left >= 255) { lacing.push(255); left -= 255; }
  lacing.push(left);
  const page = new Uint8Array(27 + lacing.length + packet.length);
  const v = new DataView(page.buffer);
  page.set([0x4f, 0x67, 0x67, 0x53], 0); // "OggS"
  page[4] = 0;
  page[5] = type;
  v.setUint32(6, granule % 0x100000000, true);
  v.setUint32(10, Math.floor(granule / 0x100000000), true);
  v.setUint32(14, serial, true);
  v.setUint32(18, seq, true);
  page[26] = lacing.length;
  page.set(lacing, 27);
  page.set(packet, 27 + lacing.length);
  v.setUint32(22, oggCrc(page), true);
  return page;
}

function defaultOpusHead(channels: number): Uint8Array {
  const h = new Uint8Array(19);
  const v = new DataView(h.buffer);
  h.set(new TextEncoder().encode('OpusHead'), 0);
  h[8] = 1;
  h[9] = channels;
  v.setUint16(10, 312, true); // pre-skip, the usual encoder delay
  v.setUint32(12, 48000, true);
  v.setUint16(16, 0, true);
  h[18] = 0;
  return h;
}

function opusTags(): Uint8Array {
  const vendor = new TextEncoder().encode('CMB Chats');
  const t = new Uint8Array(8 + 4 + vendor.length + 4);
  const v = new DataView(t.buffer);
  t.set(new TextEncoder().encode('OpusTags'), 0);
  v.setUint32(8, vendor.length, true);
  t.set(vendor, 12);
  v.setUint32(12 + vendor.length, 0, true);
  return t;
}

/** Opus packets into an Ogg Opus file, one packet a page. */
export function writeOggOpus(src: WebmOpus, serial = 0x434d4243): Uint8Array {
  const head = src.opusHead && src.opusHead.length >= 19 && new TextDecoder().decode(src.opusHead.subarray(0, 8)) === 'OpusHead'
    ? src.opusHead
    : defaultOpusHead(src.channels);
  const pages: Uint8Array[] = [oggPage(head, 0, serial, 0, 0x02), oggPage(opusTags(), 0, serial, 1, 0)];
  let granule = 0;
  src.packets.forEach((p, i) => {
    granule += opusSamples(p);
    pages.push(oggPage(p, granule, serial, i + 2, i === src.packets.length - 1 ? 0x04 : 0));
  });
  const out = new Uint8Array(pages.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of pages) { out.set(p, at); at += p.length; }
  return out;
}

/** A WebM Opus recording as an Ogg Opus file, or null when it is not one. */
export function webmToOgg(webm: Uint8Array): Uint8Array | null {
  const opus = readWebmOpus(webm);
  return opus ? writeOggOpus(opus) : null;
}
